// spam_marked_at — marking a lead as junk, and unmarking it.
//
// THE COLUMN IS NOT OURS AND MAY NOT BE THERE.
//
// `spam_marked_at timestamptz` is defined by pt-agents/db/2026-09-12-spam-marked-at.sql
// (branch gated/welcome-emails), on public.leads AND public.chat_leads. That file is
// NOT copied here and must not be: two copies of one migration is two migrations, and
// the second one to be edited is the one that is wrong. This module holds only the
// CRM's side of the contract.
//
// The contract, in full:
//   NULL          not marked
//   a timestamptz marked, at that moment
//
// A TIMESTAMP RATHER THAN A BOOLEAN, and the reason matters to this UI: the consumer
// is the welcome-email job's 24-hour delay, which skips a row marked inside that
// window. So "when" decides whether the mark landed in time, and a mark arriving
// after the append is a different outcome from one arriving before it. The CRM
// therefore shows the time, not a tick.
//
// WHY EVERYTHING HERE GUARDS ON PRESENCE.
//
// The migration is applied by hand and had not been run when this shipped. A CRM
// that renders a button writing a column that does not exist gives the recruiter an
// error they cannot act on and, worse, lets them believe a row was marked when it
// was not. So the action is HIDDEN, not disabled-with-a-tooltip, until a single
// probe says the column is there — and the probe result is what the UI keys on,
// never an assumption.

// The two tables the migration touches. A mark on one says nothing about the other:
// they are different rows about different people and there is no shared key, which
// is exactly why the migration adds the column twice rather than once.
export const SPAM_TABLES = ['leads', 'chat_leads'];

export function isSpamTable(table) {
  return SPAM_TABLES.includes(table);
}

// The patch body for a mark or an unmark. `now` is injected so a test can pin it;
// production passes nothing and gets the real clock.
//
// now() IS TAKEN CLIENT-SIDE, and that is a deliberate, documented compromise. The
// server-side alternative is a Postgres default or an RPC, and neither is available:
// this is a PATCH through PostgREST from a browser, and `spam_marked_at` has no
// default by design (a default would have backfilled every historical row). A clock
// skewed by minutes cannot change the outcome the column feeds — a 24-hour window —
// so the precision that matters survives. A clock skewed by more than a day is a
// broken machine, and the timestamp will look obviously wrong rather than subtly so.
export function spamMarkPatch(marked, now = () => new Date()) {
  return { spam_marked_at: marked ? now().toISOString() : null };
}

// True when a row is marked. Anything that is not a usable timestamp reads as NOT
// marked — including the empty string and a malformed value — because the failure
// this avoids is a row LOOKING marked to a human while the welcome job, which parses
// it as a timestamp, treats it as junk-free and emails the person anyway.
export function isSpamMarked(row) {
  const v = row && row.spam_marked_at;
  if (v === null || v === undefined || v === '') return false;
  const t = new Date(v).getTime();
  return Number.isFinite(t);
}

// FIX-AGENTS-2026-10-12 §4: the leads worker TAGS spam (spam_marked_at + raw_payload.spam = {score, reasons}) and
// identical repeats (raw_payload.duplicate_of) instead of dropping them. Either is a TAGGED row: out of the working
// list, never routed or alerted, listed under "Spam (N)", and cleared by "Not spam". Nothing is ever deleted.
export function isDuplicateTagged(row) {
  return !!(row && row.raw_payload && row.raw_payload.duplicate_of);
}
export function isSpamTagged(row) {
  return isSpamMarked(row) || isDuplicateTagged(row);
}
/** Why a row is tagged, for the pill: the spam rules, "duplicate", or "marked" (a human's mark). '' when not tagged. */
export function spamTagLabel(row) {
  if (!isSpamTagged(row)) return '';
  const reasons = row && row.raw_payload && row.raw_payload.spam && Array.isArray(row.raw_payload.spam.reasons)
    ? row.raw_payload.spam.reasons : [];
  if (isSpamMarked(row)) return reasons.length ? reasons.join(' + ') : 'marked';
  return 'duplicate';
}
/** The "Spam (N)" count: tagged rows. */
export function spamTaggedCount(rows) {
  return (Array.isArray(rows) ? rows : []).filter(isSpamTagged).length;
}
/**
 * The patch "Not spam" writes: the mark cleared and, for a duplicate, the link removed from raw_payload (with the
 * moment it was cleared kept there) -- the row then routes like any other lead. A NEW raw_payload; the row is untouched.
 */
export function notSpamPatch(row, now = () => new Date()) {
  const patch = spamMarkPatch(false);
  const raw = row && row.raw_payload && typeof row.raw_payload === 'object' ? row.raw_payload : null;
  if (raw && (raw.duplicate_of || raw.spam)) {
    const next = { ...raw, spam_cleared_at: now().toISOString() };
    delete next.duplicate_of;
    patch.raw_payload = next;
  }
  return patch;
}

export function spamMarkedAt(row) {
  return isSpamMarked(row) ? new Date(row.spam_marked_at) : null;
}

// Does a PostgREST error mean "that column is not there"?
//
// Two distinct spellings, because two distinct layers answer:
//   PGRST204  PostgREST's schema cache does not know the column (a PATCH body naming
//             an unknown column). This is the one a write hits.
//   42703     Postgres itself: undefined_column. This is the one a SELECT hits.
// Matching only one of them was the first draft's bug: the probe used a SELECT and
// the write used a PATCH, so they failed with different codes and only one was
// recognised.
export function isMissingSpamColumn(error) {
  if (!error) return false;
  const code = String(error.code || '');
  if (code === 'PGRST204' || code === '42703') return true;
  const msg = `${error.message || ''} ${error.details || ''} ${error.hint || ''}`.toLowerCase();
  if (!msg.includes('spam_marked_at')) return false;
  return msg.includes('does not exist')
      || msg.includes('could not find')
      || msg.includes('schema cache')
      || msg.includes('undefined column');
}

// Fold a completed write back into the list the view is rendering, without a refetch.
// Returns a NEW array; the input is untouched.
export function applySpamMark(rows, id, patch) {
  if (!Array.isArray(rows)) return [];
  return rows.map(r => (r && r.id === id ? { ...r, ...patch } : r));
}

// What the button says. Kept here rather than inline in the view so the two states
// cannot drift apart, and so a test can assert the unmark path is reachable — a
// mark-only control is a control a recruiter cannot correct.
export function spamButtonFor(row) {
  return isSpamTagged(row)
    ? { label: 'Not spam', next: false, title: 'Tagged as spam or a duplicate. Click to restore it to normal routing (nothing is deleted either way).' }
    : { label: 'Mark spam', next: true, title: 'Mark this lead as spam so the welcome email skips it.' };
}

// The Leads table's columns, in order, as a function of whether the mark is available.
//
// Lives here rather than inline in the view for one reason: the header, the cells and
// the expanded row's colSpan are three places that must agree, and they did not. The
// colSpan was the literal 9 while the Spam column made the table 10 wide, so the
// decisions panel under-spanned from the moment the column was added. Deriving all
// three from this list makes that class of drift unrepresentable, and exporting it
// makes the agreement testable.
export const LEAD_COLUMNS_BEFORE_SPAM = [
  'Created', 'Name', 'Email', 'Phone', 'Form', 'Source', 'SMS consent', 'Opt-out',
];

export function leadColumns(canMarkSpam) {
  return [
    ...LEAD_COLUMNS_BEFORE_SPAM,
    ...(canMarkSpam ? ['Spam'] : []),
    'Actions',
  ];
}
